// ============================================================
// pickStatus.js
// Whether an invoice has been sent to the shop to be picked, and how far the
// shop has got with it.
//
// FIELDS ON THE INVOICE
//   pickRequested     boolean — the office sent it. FALSE, or absent, means the
//                     shop never sees it.
//   pickStatus        'Pending' | 'Ready' — only meaningful once requested.
//   pickRequestedBy   display name, pickRequestedAt  ISO string
//   pickStatusBy      display name, pickStatusAt     ISO string
//
// PICKING IS NOT INVENTORY DEDUCTION. They are two different facts about the
// same invoice — "the warehouse has gathered this" and "the stock figures have
// been adjusted" — and an invoice can be either without the other. Nothing here
// reads or writes inventoryStatus, deductedInventory or the ledger, and nothing
// in inventoryLedger.js knows these fields exist. Keep it that way: conflating
// them would make one action silently do the other.
//
// This module exists so the three screens that display pick status resolve it
// the same way. A per-page copy of "missing means false" is exactly how the old
// round-up table drifted.
// ============================================================

// A server-side operator is the only way to add or remove one array element
// without reading the array first. inventoryLedger.js imports `increment` for
// the same reason.
import { arrayUnion, arrayRemove } from 'firebase/firestore';

/**
 * Colour AND text AND glyph, so the badge survives a bad monitor.
 */
// KEY vs LABEL — they deliberately differ.
//
// The KEY is the value STORED in Firestore. The LABEL is only what a screen
// says. Renaming a key would mean migrating every record already written, and
// until that migration finished every older document would resolve to nothing
// and silently show the fallback — so the stored vocabulary is frozen and the
// wording is changed here, where it costs nothing.
//
// So 'Pending' is the stored value and "Not pulled" is what everyone reads.
export const PICK_STATUS = {
  'Pending': { label: 'Not pulled', icon: '🟠', bg: '#FFF3DC', fg: '#7A4E00' },
  'Ready': { label: 'Ready', icon: '🟢', bg: '#E4F5EC', fg: '#0B5B44' },
};

/**
 * Has the office sent this invoice to the shop?
 *
 * Older invoices have none of these fields. An absent pickRequested is FALSE —
 * the shop must never inherit a backlog of every invoice ever written.
 */
export const isPickRequested = (doc) => doc?.pickRequested === true;

/**
 * 'Pending' or 'Ready'. Defaults to 'Pending', which is what an invoice the
 * shop has not touched is. Only meaningful when isPickRequested is true.
 */
export const resolvePickStatus = (doc) =>
  (doc?.pickStatus === 'Ready' ? 'Ready' : 'Pending');

/**
 * The badge to show for an invoice, or null when there is nothing to say.
 *
 * Returning null for an unrequested invoice keeps the list quiet: a badge on
 * every row would say "not sent to the shop" about hundreds of invoices nobody
 * ever intended to send.
 */
export function pickBadge(doc) {
  if (!isPickRequested(doc)) return null;
  const key = resolvePickStatus(doc);
  return { key, ...PICK_STATUS[key] };
}

// ════════════════════════ PER-LINE BACK ORDER ════════════════════════
//
// A customer collects part of an order and leaves the rest for later. The lines
// they took are ticked; the unticked ones are the back order.
//
// ⚠️ INVENTORY IS NOT INVOLVED, AND MUST NOT BE WIRED IN HERE.
// The stock for this invoice was deducted in full when the office marked it
// deducted. Ticking a line records that the CUSTOMER has physically taken it —
// a collection record, not a stock movement. Nothing in this section reads or
// writes inventoryStatus, deductedInventory or the ledger, and nothing in
// inventoryLedger.js knows pickedItemIds exists. If the two are ever joined,
// every partial collection will deduct the same stock twice.
//
// ── WHY IDS, NOT INDEXES ──
// pickedItemIds holds LINE ITEM IDS. A document can be edited after it is sent
// to the shop — a row deleted, a row moved with the up/down toolbar — and an
// index-based tick would then sit on a different product with nothing looking
// wrong. Ids survive all of it: EditDocument loads line items with { ...item },
// and update, delete and reorder all match on item.id.
//
// Ids are stored as STRINGS. The app generates them as numbers
// (Date.now() + Math.random()), and a number that has been through Firestore
// should not be compared to one that has not with ===. Normalising both sides
// removes the question.

/** The comparable key for a line item id, or null when there is nothing to key on. */
export const itemKey = (id) =>
  (id === null || id === undefined || id === '') ? null : String(id);

/** The ids already collected. An absent field, or junk, reads as nothing collected. */
export const pickedIds = (invoice) =>
  (Array.isArray(invoice?.pickedItemIds) ? invoice.pickedItemIds : []).map(String);

/**
 * Line items a customer can carry out of the building.
 *
 * Text notes are excluded — they are instructions printed among the rows, not
 * goods, and counting them would make "3 of 11 still to collect" wrong.
 */
export const collectableItems = (invoice) =>
  (Array.isArray(invoice?.lineItems) ? invoice.lineItems : []).filter(i => i && !i.isTextNote);

/** Has this line been collected? An item with no id can never be ticked. */
export function isItemPicked(item, invoice) {
  const key = itemKey(item?.id);
  if (!key) return false;
  return pickedIds(invoice).includes(key);
}

/**
 * The field set to write when a line is ticked or unticked.
 *
 * arrayUnion / arrayRemove rather than a rebuilt array: the counter and the
 * shop tablet could tick different lines in the same moment, and each writing
 * the whole list from its own stale copy would lose one of them. That is the
 * lost update the board comments had, and it is avoidable for free here
 * because both directions are single-element operations the server can do.
 *
 * Returns null when the item has no id, so a caller cannot write undefined.
 */
export function toggleItemFields(invoice, itemId) {
  const key = itemKey(itemId);
  if (!key) return null;
  return {
    pickedItemIds: pickedIds(invoice).includes(key) ? arrayRemove(key) : arrayUnion(key),
  };
}

/**
 * The same toggle as a plain array, for a caller keeping optimistic local
 * state. The WRITE still goes through toggleItemFields — this is the local
 * echo of it, never the thing sent to Firestore.
 */
export function toggledItemIds(invoice, itemId) {
  const key = itemKey(itemId);
  const current = pickedIds(invoice);
  if (!key) return current;
  return current.includes(key) ? current.filter(k => k !== key) : [...current, key];
}

/** How many collectable lines are still outstanding. */
export function backOrderCount(invoice) {
  const picked = new Set(pickedIds(invoice));
  return collectableItems(invoice).filter(i => {
    const key = itemKey(i.id);
    return !key || !picked.has(key);
  }).length;
}

/** Total collectable lines — the denominator in "3 of 11 still to collect". */
export const collectableCount = (invoice) => collectableItems(invoice).length;

/**
 * Should a back-order indicator show?
 *
 * Only for an invoice the office actually sent to the shop: by this measure
 * every invoice ever written has uncollected lines, and badging all of them
 * would say nothing. Deliberately independent of pickStatus — an order can be
 * Ready with lines outstanding (the customer took what they wanted and left the
 * rest) or Not pulled with none, and the two facts must not be conflated.
 */
export const hasBackOrder = (invoice) =>
  isPickRequested(invoice) && backOrderCount(invoice) > 0;

/** The fields written when the SHOP moves an order between Picking and Ready.
 *
 * Deliberately does NOT include pickRequested: sending and cancelling belong to
 * the office, and a welder marking something Ready must not be able to remove
 * it from his own list.
 *
 * This exists so that all five pick-field writes in the app — request, cancel,
 * and this one — are produced by this module. A per-screen copy of "which three
 * fields and in what shape" is how two writers drift into disagreeing, and the
 * shop and the office reading different truths about the same invoice is the
 * one failure this feature cannot afford.
 */
export const pickStatusFields = (displayName, status) => ({
  pickStatus: status === 'Ready' ? 'Ready' : 'Pending',
  pickStatusBy: displayName || '',
  pickStatusAt: new Date().toISOString(),
});

/** The fields written when the office sends an invoice to the shop. */
export const pickRequestFields = (displayName) => ({
  pickRequested: true,
  pickStatus: 'Pending',
  pickRequestedBy: displayName || '',
  pickRequestedAt: new Date().toISOString(),
  // The shop has not touched it yet, so any previous shop stamp is stale.
  pickStatusBy: null,
  pickStatusAt: null,
});

/**
 * The fields written when the office cancels a request.
 *
 * pickRequested goes false so the shop stops seeing it; pickStatus returns to
 * 'Pending' so a later re-send does not arrive already marked Ready. The
 * requestedBy/At stamps are cleared with it — a cancelled request is not a
 * request.
 */
export const pickCancelFields = () => ({
  pickRequested: false,
  pickStatus: 'Pending',
  pickRequestedBy: null,
  pickRequestedAt: null,
  pickStatusBy: null,
  pickStatusAt: null,
});
